import { useField } from 'formik';
import { FC } from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';

import { isCheckboxField } from '../../components/form-editor/checkbox-field';
import { isEmailInputField } from '../../components/form-editor/email-input-field';
import { isInputField } from '../../components/form-editor/input-field';
import { isPhoneInputField } from '../../components/form-editor/phone-input-field';
import { isRadioGroupField } from '../../components/form-editor/radio-group-field';
import { isSelectField } from '../../components/form-editor/select-field';
import { isTextField } from '../../components/form-editor/text-field';
import { isButtonField } from '../../components/form-editor/button-field';
import { Field } from '../../types/form-fields';
import {
  TextField,
  EmailInputField,
  InputField,
  CheckboxField,
  RadioGroupField,
  SelectField,
  PhoneInputField,
  ButtonField,
  SpacerField,
} from './form-fields';
import { isSpacerField } from '../../components/form-editor/spacer-field';
import { Branding } from './branding';
import { ApiError, TApiError } from '../../components';
import { useAppSelector } from '../../lib';

type FormProps = {
  onButtonClick?: () => void;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  gameResultError?: TApiError;
};

type GenericFieldProps = {
  formField: Field;
  name: string;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  getDisplayValue?: (value: string) => string;
  onButtonClick?: () => void;
  isCopyButton?: boolean;
  reduceSize?: boolean;
};

export const GenericField: FC<GenericFieldProps> = ({
  formField,
  name,
  buttonDisabled,
  isCopyButton,
  buttonLoading,
  reduceSize,
  getDisplayValue,
  onButtonClick,
}) => {
  const props = { name, reduceSize };

  if (isTextField(formField)) {
    return <TextField {...props} getDisplayValue={getDisplayValue} />;
  }

  if (isInputField(formField)) {
    return <InputField {...props} />;
  }

  if (isEmailInputField(formField)) {
    return <EmailInputField {...props} />;
  }

  if (isCheckboxField(formField)) {
    return <CheckboxField {...props} />;
  }

  if (isRadioGroupField(formField)) {
    return <RadioGroupField {...props} />;
  }

  if (isSelectField(formField)) {
    return <SelectField {...props} />;
  }

  if (isPhoneInputField(formField)) {
    return <PhoneInputField {...props} />;
  }

  if (isButtonField(formField)) {
    return (
      <ButtonField
        {...props}
        onClick={onButtonClick}
        isCopyButton={isCopyButton}
        disabled={buttonDisabled}
        loading={buttonLoading}
      />
    );
  }

  if (isSpacerField(formField)) {
    return <SpacerField {...props} />;
  }

  return null;
};

export const Form: FC<FormProps> = ({ onButtonClick, buttonDisabled, buttonLoading, gameResultError }) => {
  const fieldSliceName = 'campaign.formFields.fields';
  const [field] = useField<Field[]>(fieldSliceName);
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [wixInstanceIdField] = useField<string>('campaign.wixInstanceId');
  const [showBranding] = useField<boolean | undefined>('campaign.showBranding');
  const shouldReduceFieldsSize = useAppSelector((state) => state.config.reduceUISize);

  const { value } = field;

  return (
    <FormWrapper isDesktop={isDesktop}>
      {value.map((formField, index) => (
        <GenericField
          key={index}
          name={`${fieldSliceName}[${index}]`}
          formField={formField}
          onButtonClick={onButtonClick}
          buttonDisabled={buttonDisabled}
          buttonLoading={buttonLoading}
          isCopyButton={false}
          reduceSize={shouldReduceFieldsSize}
        />
      ))}

      {gameResultError && <ApiError error={gameResultError} />}

      <Branding formFields={value} showBranding={showBranding.value} wixInstanceId={wixInstanceIdField.value} />
    </FormWrapper>
  );
};

const FormWrapper = styled.div<{ isDesktop: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${(p) => (p.isDesktop ? 12 : 9)}px;
  padding: 4px;
`;
